import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromUserSetting from '../../user-setting/store';

@Injectable({
    providedIn: 'root'
})
export class MessageStateService {
    private _markAsRead = new BehaviorSubject<boolean>(false);
    private _messageCount = new BehaviorSubject<number>(0);
    private _isInitialized = false;
    private _pendingUpdate = false;

    // Public observables
    public readonly markAsRead$: Observable<boolean> = this._markAsRead.asObservable();
    public readonly messageCount$: Observable<number> = this._messageCount.asObservable();
    public readonly showBadge$: Observable<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private store: Store) {
        // Subscribe to user settings to get the mark as read state
        this.store.select(fromUserSetting.selectAll).subscribe(userSettings => {
            if (userSettings.length > 0 && userSettings[0].EmpSettingValue != null) {
                // Setting value '1' means show messages (not marked as read)
                // Setting value '0' means hide messages (marked as read)
                const isMarkedAsRead = userSettings[0].EmpSettingValue === '0';

                // On first load, always initialize from store
                if (!this._isInitialized) {
                    this._markAsRead.next(isMarkedAsRead);
                    this._isInitialized = true;
                    this.updateShowBadge();
                    return;
                }

                // After initialization, only update if not in the middle of a pending update
                // This prevents the store from overriding local changes during PUT operations
                if (!this._pendingUpdate && this._markAsRead.value !== isMarkedAsRead) {
                    this._markAsRead.next(isMarkedAsRead);
                    this.updateShowBadge();
                }

                // Clear pending flag once store has been updated
                if (this._pendingUpdate && this._markAsRead.value === isMarkedAsRead) {
                    this._pendingUpdate = false;
                }
            }
        });
    }

    // Initialize the service by loading user settings from the store
    public initializeFromStore(employeeListID: string): void {
        this.store.dispatch(
            fromUserSetting.getUserSettings({
                EmployeeListID: employeeListID
            })
        );
    }

    // Get current values
    get markAsRead(): boolean {
        return this._markAsRead.value;
    }

    set markAsRead(value: boolean) {
        this._markAsRead.next(value);
        this.updateShowBadge();
    }

    get messageCount(): number {
        return this._messageCount.value;
    }

    get showBadge(): boolean {
        return this.messageCount > 0 && !this.markAsRead;
    }

    // Update message count
    setMessageCount(count: number): void {
        this._messageCount.next(count);
        this.updateShowBadge();
    }

    // Update mark as read setting
    setMarkAsRead(markAsRead: boolean, employeeListID: string): void {
        // Set pending flag to prevent store subscription from overriding during update
        this._pendingUpdate = true;

        // Update the local state immediately for immediate UI feedback
        this.markAsRead = markAsRead;

        // Convert boolean to setting value ('0' for marked as read, '1' for not marked as read)
        const settingValue = markAsRead ? '0' : '1';

        this.store.dispatch(
            fromUserSetting.putUserSettings({
                settings: [
                    {
                        EmployeeListID: employeeListID,
                        EmpSettingID: 31.0,
                        EmpSettingType: 'ShowUserMsg',
                        EmpSettingTypeLabel: 'Show User Message',
                        EmpSettingValue: settingValue,
                        CtrlType: 'CheckBox',
                        CatID: 1,
                        CatDescription: 'User Setting'
                    }
                ]
            })
        );
    }

    private updateShowBadge(): void {
        const shouldShow = this.messageCount > 0 && !this.markAsRead;
        (this.showBadge$ as BehaviorSubject<boolean>).next(shouldShow);
    }
}
